import React, { useState, useEffect, useRef } from "react";
import { FileSignature, Printer, Send, Download } from "lucide-react";
import { getSystemConfig } from "../services/systemConfigService";
import { getSignatures } from "../services/signatureService";
import html2pdf from "html2pdf.js";

// ─── Shared style helpers ─────────────────────────────────────────────────────
const inputCls =
  "border border-gray-400 rounded px-1.5 py-0.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500";
const inlineInput = (extraCls = "") => `${inputCls} inline-block ${extraCls}`;

// ─── PDF box style ────────────────────────────────────────────────────────────
const pdfBox = (val, { minW = 120, flex = false, bold = false, multiline = false } = {}) => ({
  display: flex ? "block" : "inline-block",
  border: "1px solid #9ca3af",
  borderRadius: 4,
  background: "#f8fafc",
  padding: multiline ? "8px 12px" : "4px 10px",
  minWidth: flex ? "100%" : minW,
  minHeight: multiline ? 54 : 30,
  fontSize: 15,
  fontWeight: bold ? 600 : 400,
  color: val ? "#111827" : "#9ca3af",
  fontFamily: "Arial, Helvetica, sans-serif",
  whiteSpace: multiline ? "pre-wrap" : "normal",
  verticalAlign: "middle",
  lineHeight: multiline ? 1.6 : "26px",
  boxSizing: "border-box",
});

// ─── Date helpers ─────────────────────────────────────────────────────────────
const ddmmyyyyToISO = (str) => {
  if (!str) return "";
  const parts = str.split("/");
  if (parts.length !== 3) return "";
  const [d, m, y] = parts;
  if (!d || !m || !y) return "";
  return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
};

const isoToDDMMYYYY = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return "";
  return `${d}/${m}/${y}`;
};

const formatToDDMMYYYY = (date) => {
  if (!date) return "";
  const parsedDate = new Date(date);
  if (isNaN(parsedDate)) return date;
  const day = String(parsedDate.getDate()).padStart(2, "0");
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const year = parsedDate.getFullYear();
  return `${day}/${month}/${year}`;
};

const parseDDMMYYYY = (str) => {
  if (!str) return null;
  const parts = str.split("/");
  if (parts.length !== 3) return null;
  const [d, m, y] = parts.map(Number);
  if (!d || !m || !y) return null;
  const date = new Date(y, m - 1, d);
  return isNaN(date) ? null : date;
};

const addMonthsToDDMMYYYY = (ddmmyyyy, months) => {
  const start = parseDDMMYYYY(ddmmyyyy);
  if (!start || !months) return "";
  const end = new Date(start);
  end.setMonth(end.getMonth() + parseInt(months, 10));
  const day = String(end.getDate()).padStart(2, "0");
  const month = String(end.getMonth() + 1).padStart(2, "0");
  const year = end.getFullYear();
  return `${day}/${month}/${year}`;
};

// ─── DatePicker ───────────────────────────────────────────────────────────────
const DatePicker = ({
  value,
  onChange,
  placeholder = "dd/mm/yyyy",
  className = "",
  readOnly = false,
  title = "",
  isPdfMode = false,
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const inputRef = useRef(null);
  const isoValue = ddmmyyyyToISO(value);

  if (isPdfMode) {
    return (
      <span style={pdfBox(value, { minW: 130 })}>
        {value || <span style={{ color: "#9ca3af" }}>dd/mm/yyyy</span>}
      </span>
    );
  }

  if (readOnly) {
    return (
      <input
        className={`${inputCls} bg-gray-50 ${className}`}
        value={value}
        readOnly
        placeholder={placeholder}
        title={title}
      />
    );
  }

  const handleDateChange = (e) => {
    onChange(isoToDDMMYYYY(e.target.value));
    setShowPicker(false);
  };

  return (
    <span className="relative inline-block" style={{ verticalAlign: "middle" }}>
      <input
        className={`${inputCls} pr-7 ${className}`}
        value={value}
        placeholder={placeholder}
        title={title}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setShowPicker(true)}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => {
          setShowPicker((s) => !s);
          setTimeout(() => inputRef.current?.showPicker?.(), 50);
        }}
        className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-500 focus:outline-none"
        aria-label="Open date picker"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </button>
      <input
        ref={inputRef}
        type="date"
        value={isoValue}
        onChange={handleDateChange}
        onBlur={() => setShowPicker(false)}
        className="absolute inset-0 opacity-0 pointer-events-none w-full h-full"
        tabIndex={-1}
        style={{ zIndex: showPicker ? 10 : -1, pointerEvents: showPicker ? "auto" : "none" }}
      />
    </span>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const buildEquipmentDesc = (hardwareItems = [], additionalHardware = []) => {
  const lines = [];
  [...hardwareItems, ...additionalHardware].forEach((item) => {
    if ((item.quantity ?? 0) > 0) lines.push(`${item.quantity}x ${item.name}`);
  });
  return lines.join("\n");
};

const buildTotalEquipmentQty = (hardwareItems = [], additionalHardware = []) => {
  const mobileAppUsers = hardwareItems.reduce(
    (total, item) =>
      item.name === "Mobile App" || item.name === "Mobile App Addon"
        ? total + (item.quantity ?? 0)
        : total,
    0
  );
  const hwCount = hardwareItems
    .filter((h) => h.name !== "Mobile App" && h.name !== "Mobile App Addon")
    .reduce((sum, h) => sum + (h.quantity ?? 0), 0);
  const addlCount = additionalHardware.reduce((sum, h) => sum + (h.quantity ?? 0), 0);
  return String(mobileAppUsers + hwCount + addlCount);
};
const buildProductsSectionTotalQty = (hardwareItems = [], additionalHardware = []) => {
  const totalQty = (item) => (item?.quantity || 0) + (item?.focQuantity || 0);
  const find = (arr, name) => arr.find((i) => i.name === name) || { quantity: 0, focQuantity: 0 };
  const mobileAppUsers = hardwareItems.reduce((total, item) => {
    if (item.name === "Mobile App" || item.name === "Mobile App Addon")
      return total + (item.quantity || 0) + (item.focQuantity || 0);
    return total;
  }, 0);

  const t54w = find(hardwareItems, "T54W");
  const t57w = find(hardwareItems, "T57W");
  const t58w = find(hardwareItems, "T58W");
  const t85 = find(hardwareItems, "YEALINK T85");
  const t87 = find(hardwareItems, "YEALINK T87");
  const t88 = find(hardwareItems, "YEALINK T88");
  const grandstream3470 = find(hardwareItems, "Grandstream 3470");
  const grandstream3480 = find(hardwareItems, "Grandstream 3480");
  const cisco = find(hardwareItems, "Cisco 9871");
  const ipDectHandset = find(hardwareItems, "Yealink W73H DECT");

  const ucmpbx = find(additionalHardware, "UCM IP PBX");
  const wifi = find(additionalHardware, "Draytek 2865LAC");
  const poe = find(additionalHardware, "Netgear GS108LP");

  let total = 0;

  // Rows whose QTY cell = quantity + FOC
  if (mobileAppUsers > 0) total += mobileAppUsers;
  if (totalQty(t54w) > 0) total += totalQty(t54w);
  if (totalQty(t57w) > 0) total += totalQty(t57w);
  if (totalQty(t58w) > 0) total += totalQty(t58w);
  if (totalQty(t88) > 0) total += totalQty(t88);
  if (totalQty(t85) > 0) total += totalQty(t85);
  if (totalQty(t87) > 0) total += totalQty(t87);
  if (totalQty(grandstream3470) > 0) total += totalQty(grandstream3470);
  if (totalQty(grandstream3480) > 0) total += totalQty(grandstream3480);

  // Rows whose QTY cell = quantity only (FOC not shown, matches the JSX exactly)
  if (totalQty(cisco) > 0) total += cisco.quantity || 0;
  if (totalQty(ipDectHandset) > 0) total += ipDectHandset.quantity || 0;
  if (ucmpbx.quantity > 0) total += ucmpbx.quantity;
  if (wifi.quantity > 0) total += wifi.quantity;
  if (poe.quantity > 0) total += poe.quantity;

  total += 1; // Fibre Installation row — always shown, qty 1

  if (mobileAppUsers > 0) total += mobileAppUsers * 3; // Licence + Call Reporting + Call Recording rows

  return String(total);
};

const SignatureBox = ({ url, isPdfMode }) => {
  if (url) {
    return (
      <img
        src={url}
        alt="Signature"
        crossOrigin="anonymous"
        style={{ height: 40, maxWidth: 180, objectFit: "contain" }}
      />
    );
  }
  return isPdfMode
    ? <div style={{ flex: 1, borderBottom: "1px solid #374151", height: 22 }} />
    : <div className="flex-1 border-b border-gray-400 h-6" />;
};

// ─── Shared PDF document wrapper style ───────────────────────────────────────
// ─── Shared PDF document wrapper style ───────────────────────────────────────
const pdfDocStyle = {
  background: "#ffffff",
  padding: "54px 62px", // was 44px 52px — increased ~10px/~1cm to compensate for removed outer PDF margin
  fontFamily: "Arial, Helvetica, sans-serif",
  fontSize: 15,
  color: "#1a1a1a",
  lineHeight: 1.7,
  maxWidth: 780,
  margin: "0 auto",
};

// ─── Logo header ──────────────────────────────────────────────────────────────
const FibreHeader = ({ isPdfMode }) => (
  <div
    style={isPdfMode ? { display: "flex", justifyContent: "flex-end", marginBottom: 24 } : undefined}
    className={isPdfMode ? undefined : "flex justify-end mb-6"}
  >
    <img
      src="https://quoting-tool.fibretelecomsuk.co.uk/images/logo-vertical.jpg"
      alt="Fibre Telecoms UK"
      style={isPdfMode ? { width: 155, height: "auto", objectFit: "contain" } : undefined}
      className={isPdfMode ? undefined : "w-48 h-auto object-contain rounded-lg bg-white p-2"}
      crossOrigin="anonymous"
    />
  </div>
);

const SectionDivider = ({ title }) => (
  <div className="flex items-center gap-3 my-8">
    <div className="flex-1 h-px bg-gray-300" />
    <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest px-2">{title}</span>
    <div className="flex-1 h-px bg-gray-300" />
  </div>
);

const PdfHr = ({ style = {} }) => (
  <hr style={{ border: "none", borderTop: "1px solid #d1d5db", margin: "16px 0", ...style }} />
);

const PdfInlineBox = ({ label, value, minW = 130, bold = false }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, verticalAlign: "middle" }}>
    {label && <span>{label}</span>}
    <span style={pdfBox(value, { minW, bold })}>
      {value || <span style={{ color: "#9ca3af", fontStyle: "italic", fontSize: 12 }}>—</span>}
    </span>
  </span>
);

// ═══════════════════════════════════════════════════════════════════════════════
// DOCUMENT 1 — Form of Offer
// ═══════════════════════════════════════════════════════════════════════════════
const FormOfOffer = ({ data, onChange, isPdfMode, sig1Url, sig1Name }) => {
  if (isPdfMode) {
    return (
      <div style={pdfDocStyle}>
        <FibreHeader isPdfMode />
        <h2 style={{ fontSize: 29, fontWeight: 300, color: "#1f2937", marginBottom: 28, letterSpacing: -0.3 }}>
          Form of Offer
        </h2>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 28 }}>
          <div style={{ fontSize: 15, lineHeight: 1.9 }}>
            <div>To:&nbsp;&nbsp;&nbsp;CF Corporate Finance Limited</div>
            <div style={{ marginLeft: 46 }}>Raynham Road</div>
            <div style={{ marginLeft: 46 }}>Bishop's Stortford</div>
            <div style={{ marginLeft: 46 }}>Herts CM23 5TT</div>
          </div>
          <div style={{ fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
            <span>Date</span>
            <span style={pdfBox(data.offerDate, { minW: 140 })}>
              {data.offerDate || <span style={{ color: "#9ca3af", fontStyle: "italic" }}>dd/mm/yyyy</span>}
            </span>
          </div>
        </div>
        <p style={{ fontSize: 15, marginBottom: 20 }}>Dear Sirs,</p>
        <p style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>
          Master sale and purchase agreement dated{" "}
          <span style={pdfBox(data.agreementDate, { minW: 160 })}>
            {data.agreementDate || <span style={{ color: "#9ca3af", fontStyle: "italic" }}>date</span>}
          </span>
          {" "}<span style={{ fontWeight: 700 }}>(Agreement)</span>
        </p>
        <p style={{ fontSize: 15, marginBottom: 20 }}>We refer to the Agreement and hereby offer to:</p>
        <div style={{ fontSize: 15, marginBottom: 20 }}>
          <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
            <span style={{ minWidth: 20 }}>1</span>
            <p style={{ margin: 0 }}>sell to you upon the terms set out in the Agreement and the attached Offer Letter Schedule, the Equipment; and</p>
          </div>
          <div style={{ display: "flex", gap: 16 }}>
            <span style={{ minWidth: 20 }}>2</span>
            <p style={{ margin: 0 }}>assign to you with full title guarantee absolutely the Contract Rights in respect of the Contract detailed in the attached Offer Letter Schedule,</p>
          </div>
        </div>
        <p style={{ fontSize: 15, marginBottom: 20 }}>
          at the price of £{" "}
          <span style={pdfBox(data.offerPrice, { minW: 120, bold: true })}>
            {data.offerPrice || <span style={{ color: "#9ca3af", fontStyle: "italic" }}>0.00</span>}
          </span>
          {" "}plus VAT <strong>(Offer Price)</strong>
        </p>
        <p style={{ fontSize: 15, marginBottom: 32 }}>
          This offer may only be accepted in accordance with clause 2 (Offers to sell Equipment and Contract Rights) of the Agreement. Terms not otherwise defined in this letter and the Offer Letter Schedule shall have the same meaning as in the Agreement.
        </p>
        <p style={{ fontSize: 15, marginBottom: 24 }}>Yours faithfully</p>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <span style={{ minWidth: 60 }}>Signed</span>
          <SignatureBox url={sig1Url} isPdfMode />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
          <span style={{ minWidth: 60 }}>Name</span>
          <span style={pdfBox(sig1Name, { minW: 200 })}>
            {sig1Name || <span style={{ color: "#9ca3af", fontStyle: "italic", fontSize: 12 }}>—</span>}
          </span>
        </div>

        <div style={{ fontSize: 15 }}>
          <p>
            for and on behalf of{" "}
            <span style={pdfBox(data.sellerName, { minW: 220 })}>
              {data.sellerName || <span style={{ color: "#9ca3af", fontStyle: "italic" }}>company name</span>}
            </span>
            {" "}<strong>(Seller)</strong>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm font-serif text-sm leading-relaxed">
      <FibreHeader />
      <h2 className="text-3xl font-light text-gray-800 mb-8">Form of Offer</h2>
      <div className="flex justify-between items-start mb-8">
        <div>
          <p className="font-sans text-sm">To:&nbsp;&nbsp;CF Corporate Finance Limited</p>
          <p className="font-sans text-sm ml-12">Raynham Road</p>
          <p className="font-sans text-sm ml-12">Bishop's Stortford</p>
          <p className="font-sans text-sm ml-12">Herts CM23 5TT</p>
        </div>
        <div className="flex items-center gap-2 font-sans text-sm">
          <span>Date</span>
          <DatePicker value={data.offerDate} onChange={(v) => onChange("offerDate", v)} className="w-40" />
        </div>
      </div>
      <p className="font-sans text-sm mb-6">Dear Sirs,</p>
      <div className="flex items-center gap-2 font-sans text-sm font-bold mb-4 flex-wrap">
        <span>Master sale and purchase agreement dated</span>
        <input
          className={inlineInput("w-40 font-normal")}
          value={data.agreementDate}
          onChange={(e) => onChange("agreementDate", e.target.value)}
          placeholder="2nd June 2026"
        />
        <span className="font-bold">(Agreement)</span>
      </div>
      <p className="font-sans text-sm mb-6">We refer to the Agreement and hereby offer to:</p>
      <div className="space-y-4 mb-6 font-sans text-sm">
        <div className="flex gap-4"><span className="w-6 shrink-0">1</span><p>sell to you upon the terms set out in the Agreement and the attached Offer Letter Schedule, the Equipment; and</p></div>
        <div className="flex gap-4"><span className="w-6 shrink-0">2</span><p>assign to you with full title guarantee absolutely the Contract Rights in respect of the Contract detailed in the attached Offer Letter Schedule,</p></div>
      </div>
      <div className="flex items-center gap-2 font-sans text-sm mb-6 flex-wrap">
        <span>at the price of £</span>
        <input className={inlineInput("w-36")} value={data.offerPrice} onChange={(e) => onChange("offerPrice", e.target.value)} placeholder="0.00" />
        <span>plus VAT <strong>(Offer Price)</strong></span>
      </div>
      <p className="font-sans text-sm mb-8">This offer may only be accepted in accordance with clause 2 (Offers to sell Equipment and Contract Rights) of the Agreement. Terms not otherwise defined in this letter and the Offer Letter Schedule shall have the same meaning as in the Agreement.</p>
      <p className="font-sans text-sm mb-6">Yours faithfully</p>

      <div className="flex items-center gap-2 mb-2">
        <span className="w-16 shrink-0 text-xs">Signed</span>
        <SignatureBox url={sig1Url} />
      </div>
      <div className="flex items-center gap-2 mb-8">
        <span className="w-16 shrink-0 text-xs">Name</span>
        <span className="text-sm">{sig1Name}</span>
      </div>

      <div className="flex items-center gap-2 font-sans text-sm flex-wrap">
        <span>for and on behalf of</span>
        <input className={inlineInput("w-64")} value={data.sellerName} onChange={(e) => onChange("sellerName", e.target.value)} placeholder="Fibre Telecoms UK Limited" />
        <span className="font-bold">(Seller)</span>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// DOCUMENT 2 — Details of the Offer / Schedule
// ═══════════════════════════════════════════════════════════════════════════════
const DetailsOfOffer = ({ data, onChange, termMonths, isPdfMode }) => {
  const handlePaymentDateChange = (val) => {
    onChange("paymentDate", val);
    onChange("leaseFrom", val);
    onChange("leaseTo", addMonthsToDDMMYYYY(val, termMonths));
  };

  if (isPdfMode) {
    // Tighter doc wrapper just for this page — smaller margins/font than the shared pdfDocStyle
   const compactDocStyle = {
      ...pdfDocStyle,
      padding: "36px 50px", // was 26px 40px — increased to compensate for removed outer PDF margin
      fontSize: 13,
      lineHeight: 1.4,
    };

    const row = { display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 7, fontSize: 13 };
    const num = { minWidth: 18, fontWeight: 700, paddingTop: 2, flexShrink: 0 };

    // Compact answer box — same look as pdfBox but smaller padding/line-height
    const box = (val, { minW = 110, bold = false, multiline = false, flex = false } = {}) => ({
      display: flex ? "block" : "inline-block",
      border: "1px solid #9ca3af",
      borderRadius: 4,
      background: "#f8fafc",
      padding: multiline ? "5px 8px" : "2px 8px",
      minWidth: flex ? "100%" : minW,
      minHeight: multiline ? 32 : 20,
      fontSize: 13,
      fontWeight: bold ? 600 : 400,
      color: val ? "#111827" : "#9ca3af",
      fontFamily: "Arial, Helvetica, sans-serif",
      whiteSpace: multiline ? "pre-wrap" : "normal",
      verticalAlign: "middle",
      lineHeight: multiline ? 1.35 : "18px",
      boxSizing: "border-box",
    });

    const B = ({ v, minW = 110, bold = false }) => (
      <span style={box(v, { minW, bold })}>
        {v || <span style={{ color: "#9ca3af", fontStyle: "italic", fontSize: 11 }}>—</span>}
      </span>
    );

    return (
      <div style={compactDocStyle}>
        <FibreHeader isPdfMode />
        <div style={{ fontSize: 13, marginBottom: 6 }}>
          Schedule dated <B v={data.scheduleDated} />
          {" "}to an Offer Letter dated <B v={data.offerLetterDated} />
        </div>
        <h2 style={{ fontSize: 19, fontWeight: 300, color: "#1f2937", marginBottom: 4 }}>Details of the Offer</h2>
        <PdfHr style={{ margin: "8px 0" }} />

        <div style={row}>
          <span style={num}>1</span>
          <div style={{ flex: 1, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
            <span>Contract ref. no.</span>
            <B v={data.contractRef} minW={130} />
            <span>dated</span>
            <B v={data.contractDated} />
          </div>
        </div>

        <div style={row}>
          <span style={num}>2</span>
          <div>
            Service Agreement relating to the Contract or the Equipment?{" "}
            <span style={{ ...box(true, { minW: 50 }), fontWeight: 700, textAlign: "center" }}>
              {data.serviceAgreement === "yes" ? "Yes" : "No"}
            </span>
          </div>
        </div>

        <div style={row}>
          <span style={num}>3</span>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 3 }}>Lessee:</div>
            <div style={box(data.lessee, { flex: true, multiline: true })}>
              {data.lessee || <span style={{ color: "#9ca3af", fontStyle: "italic" }}>—</span>}
            </div>
          </div>
        </div>

        <div style={row}>
          <span style={num}>4</span>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <span>Surety:</span>
              <B v={data.surety} minW={200} />
            </div>
            <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2 }}>(N.B. form of guarantee attached)</div>
          </div>
        </div>

        <div style={{ ...row, alignItems: "flex-start" }}>
          <span style={num}>5</span>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 4 }}>Equipment:</div>
            {[
              { label: "Make:", v: data.equipmentMake },
              { label: "Model:", v: data.equipmentModel },
              { label: "Quantity:", v: data.quantity },
            ].map(({ label, v }) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3, marginLeft: 16 }}>
                <span style={{ minWidth: 60 }}>{label}</span>
                <B v={v} minW={180} />
              </div>
            ))}
          </div>
        </div>

        <div style={row}>
          <span style={num}>6</span>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, flexWrap: "wrap" }}>
            <span>Supplier:</span>
            <B v={data.supplier} minW={240} />
          </div>
        </div>

        <div style={row}>
          <span style={num}>7</span>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span>Offer Price (exclusive of VAT): £</span>
            <B v={data.offerPriceExVat} minW={100} bold />
          </div>
        </div>

        <div style={row}>
          <span style={num}>8</span>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span>Payment Date:</span>
            <B v={data.paymentDate} minW={130} />
          </div>
        </div>

        <div style={row}>
          <span style={num}>9</span>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span>Lease period: from</span>
            <B v={data.leaseFrom} />
            <span>to</span>
            <B v={data.leaseTo} />
            {termMonths && data.leaseFrom && (
              <span style={{ fontSize: 10, color: "#6b7280" }}>({Math.round(termMonths / 12)} yr term)</span>
            )}
          </div>
        </div>

        <div style={row}>
          <span style={num}>10</span>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span>Payments: £</span>
            <B v={data.payments} minW={90} bold />
            <span style={{ ...box(true, { minW: 80 }), fontWeight: 700, textAlign: "center" }}>
              {data.paymentMonthly ? "Monthly" : data.paymentQuarterly ? "Quarterly" : data.paymentAnnually ? "Annually" : "—"}
            </span>
          </div>
        </div>

        <div style={row}>
          <span style={num}>11</span>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span>Maintenance: £</span>
            <B v={data.maintenance} minW={80} />
            <span>plus VAT</span>
          </div>
        </div>

        <div style={row}>
          <span style={num}>12</span>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span>Documentation Fee: £</span>
            <B v={data.docFee} minW={80} />
            <span>plus VAT</span>
          </div>
        </div>

        <div style={row}>
          <span style={num}>13</span>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span>Direct Debit:</span>
            <span style={{ ...box(true, { minW: 50 }), fontWeight: 700, textAlign: "center" }}>
              {data.directDebit === "yes" ? "Yes" : "No"}
            </span>
          </div>
        </div>

        <div style={{ ...row, alignItems: "flex-start", marginBottom: 0 }}>
          <span style={num}>14</span>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 3 }}>Special conditions: <em style={{ color: "#6b7280" }}>[If applicable]</em></div>
            <div style={{
              ...box(data.specialConditions, { flex: true, multiline: true }),
              minHeight: data.specialConditions ? "auto" : 30,
              height: "auto",
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}>
              {data.specialConditions || <span style={{ color: "#9ca3af", fontStyle: "italic" }}>—</span>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm font-sans text-sm leading-relaxed">
      <FibreHeader />
      <div className="flex items-center gap-2 text-sm mb-2 flex-wrap">
        <span>Schedule dated</span>
        <DatePicker value={data.scheduleDated} onChange={(v) => onChange("scheduleDated", v)} className="w-36" />
        <span>to an Offer Letter dated</span>
        <DatePicker value={data.offerLetterDated} onChange={(v) => onChange("offerLetterDated", v)} className="w-36" />
      </div>
      <h2 className="text-2xl font-light text-gray-800 mb-4">Details of the Offer</h2>
      <hr className="border-gray-400 mb-6" />
      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="w-6 shrink-0">1</span>
          <span>Contract ref. no.</span>
          <input className={inlineInput("w-40")} value={data.contractRef} onChange={(e) => onChange("contractRef", e.target.value)} />
          <span>dated</span>
          <DatePicker value={data.contractDated} onChange={(v) => onChange("contractDated", v)} className="w-36" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="w-6 shrink-0">2</span>
          <span>Service Agreement relating to the Contract or the Equipment?</span>
          <label className="flex items-center gap-1 ml-2"><input type="radio" name="serviceAgreement" value="yes" checked={data.serviceAgreement === "yes"} onChange={() => onChange("serviceAgreement", "yes")} /> Yes</label>
          <label className="flex items-center gap-1 ml-3"><input type="radio" name="serviceAgreement" value="no" checked={data.serviceAgreement === "no"} onChange={() => onChange("serviceAgreement", "no")} /> No</label>
        </div>
        <div className="flex items-start gap-2">
          <span className="w-6 shrink-0 mt-1">3</span>
          <span className="mt-1 shrink-0">Lessee:</span>
          <textarea className={`${inputCls} flex-1 h-20 resize-none`} value={data.lessee} onChange={(e) => onChange("lessee", e.target.value)} />
        </div>
        <div className="flex items-start gap-2">
          <span className="w-6 shrink-0 mt-1">4</span>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span>Surety:</span>
              <input className={inlineInput("flex-1")} value={data.surety} onChange={(e) => onChange("surety", e.target.value)} />
            </div>
            <p className="text-xs text-gray-500 mt-1">(N.B. form of guarantee attached)</p>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <span className="w-6 shrink-0 mt-1">5</span>
          <div className="flex-1 space-y-2">
            <span>Equipment:</span>
            {[
              { label: "Make:", key: "equipmentMake", w: "flex-1" },
              { label: "Model:", key: "equipmentModel", w: "flex-1" },
              { label: "Quantity:", key: "quantity", w: "w-24" },
            ].map(({ label, key, w }) => (
              <div key={key} className="flex items-center gap-2 ml-4">
                <span className="w-16">{label}</span>
                <input className={inlineInput(w)} value={data[key]} onChange={(e) => onChange(key, e.target.value)} />
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="w-6 shrink-0">6</span>
          <span>Supplier:</span>
          <input className={inlineInput("flex-1")} value={data.supplier} onChange={(e) => onChange("supplier", e.target.value)} placeholder="Fibre Telecoms UK Limited" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="w-6 shrink-0">7</span>
          <span>Offer Price (exclusive of VAT): £</span>
          <input className={inlineInput("w-36")} value={data.offerPriceExVat} onChange={(e) => onChange("offerPriceExVat", e.target.value)} placeholder="0.00" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="w-6 shrink-0">8</span>
          <span>Payment Date:</span>
          <DatePicker value={data.paymentDate} onChange={handlePaymentDateChange} className="w-40" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="w-6 shrink-0">9</span>
          <span>Lease period: from</span>
          <DatePicker value={data.leaseFrom} onChange={() => {}} readOnly className="w-36" />
          <span>to</span>
          <DatePicker value={data.leaseTo} onChange={() => {}} readOnly className="w-36" />
          {termMonths && data.leaseFrom && <span className="text-xs text-gray-400 ml-1">({Math.round(termMonths / 12)} yr term)</span>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="w-6 shrink-0">10</span>
          <span>Payments: £</span>
          <input className={inlineInput("w-32")} value={data.payments} onChange={(e) => onChange("payments", e.target.value)} placeholder="0.00" />
          <label className="flex items-center gap-1 ml-2"><input type="checkbox" checked={data.paymentMonthly} onChange={(e) => onChange("paymentMonthly", e.target.checked)} /> monthly</label>
          <label className="flex items-center gap-1 ml-2"><input type="checkbox" checked={data.paymentQuarterly} onChange={(e) => onChange("paymentQuarterly", e.target.checked)} /> quarterly</label>
          <label className="flex items-center gap-1 ml-2"><input type="checkbox" checked={data.paymentAnnually} onChange={(e) => onChange("paymentAnnually", e.target.checked)} /> annually</label>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="w-6 shrink-0">11</span>
          <span>Maintenance £</span>
          <input className={inlineInput("w-28")} value={data.maintenance} onChange={(e) => onChange("maintenance", e.target.value)} placeholder="0.00" />
          <span>plus VAT</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="w-6 shrink-0">12</span>
          <span>Documentation Fee: £</span>
          <input className={inlineInput("w-28")} value={data.docFee} onChange={(e) => onChange("docFee", e.target.value)} placeholder="0.00" />
          <span>plus VAT</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="w-6 shrink-0">13</span>
          <span>Direct Debit:</span>
          <label className="flex items-center gap-1 ml-2"><input type="radio" name="directDebit" value="yes" checked={data.directDebit === "yes"} onChange={() => onChange("directDebit", "yes")} /> Yes</label>
          <label className="flex items-center gap-1 ml-3"><input type="radio" name="directDebit" value="no" checked={data.directDebit === "no"} onChange={() => onChange("directDebit", "no")} /> No</label>
        </div>
        <div className="flex items-start gap-2">
          <span className="w-6 shrink-0 mt-1">14</span>
          <div className="flex-1">
            <p className="mb-1">Special conditions: [If applicable]</p>
            <textarea className={`${inputCls} w-full h-24 resize-none`} value={data.specialConditions} onChange={(e) => onChange("specialConditions", e.target.value)} />
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// DOCUMENT 3 — Form of Assignment
// ═══════════════════════════════════════════════════════════════════════════════
const FormOfAssignment = ({ data, onChange, schedule, isPdfMode, sig1Url, sig2Url }) => {
  if (isPdfMode) {
    const B = ({ v, minW = 160, bold = false }) => (
      <span style={pdfBox(v, { minW, bold })}>
        {v || <span style={{ color: "#9ca3af", fontStyle: "italic", fontSize: 12 }}>—</span>}
      </span>
    );

    return (
      <div style={pdfDocStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <h2 style={{ fontSize: 29, fontWeight: 300, color: "#1f2937", marginBottom: 10 }}>Form of Assignment</h2>
            <div style={{ fontSize: 15, fontWeight: 700 }}>
              This Assignment is made on <B v={data.assignmentDate} minW={150} />
            </div>
          </div>
          <FibreHeader isPdfMode />
        </div>

        <p style={{ fontWeight: 700, marginBottom: 12, fontSize: 15 }}>Between</p>

        <div style={{ display: "flex", gap: 12, marginBottom: 16, fontSize: 15 }}>
          <span style={{ minWidth: 24 }}>(1)</span>
          <div style={{ flex: 1 }}>
            <B v={data.sellerEntity} minW={250} />{" "}whose registered office is at
            <div style={{ marginTop: 4 }}>{data.sellerAddress}</div>
            <strong>(Seller) and</strong>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginBottom: 16, fontSize: 15 }}>
          <span style={{ minWidth: 24 }}>(2)</span>
          <p style={{ margin: 0 }}>CF Corporate Finance Limited (Company Number 05414774) whose registered office is at 2 Gresham Street, London, EC2V 7QP <strong>(Buyer, which definition shall include its assignees and successors).</strong></p>
        </div>

        <p style={{ fontWeight: 700, marginBottom: 12, fontSize: 15 }}>Whereas</p>

        <div style={{ display: "flex", gap: 12, marginBottom: 16, fontSize: 15 }}>
          <span style={{ minWidth: 24 }}>(A)</span>
          <div style={{ flex: 1 }}>
            <p style={{ margin: "0 0 12px" }}>By a sale and purchase agreement dated <strong>2nd June 2026</strong> <strong>(Sale Agreement)</strong> the Seller has agreed to sell and the Buyer has agreed to purchase the Equipment and the Contract Rights and certain other rights and benefits described in the Sale Agreement in respect of the Contract as set out below:</p>
            <div style={{ marginLeft: 16, display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ minWidth: 148, fontSize: 15 }}>Agreement Number</span>
                <B v={data.agreementNumber} minW={180} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ minWidth: 148, fontSize: 15 }}>Equipment (Model)</span>
                <B v={schedule?.equipmentModel} minW={220} />
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginBottom: 20, fontSize: 15 }}>
          <span style={{ minWidth: 24 }}>(B)</span>
          <p style={{ margin: 0 }}>Under the terms of the Sale Agreement, this Assignment is to be delivered to the Buyer.</p>
        </div>

        <PdfHr />

        <div style={{ fontSize: 15, marginBottom: 16 }}>
          <p style={{ fontWeight: 700, marginBottom: 8 }}>1&nbsp;&nbsp;&nbsp;Definitions and interpretation</p>
          <p style={{ marginLeft: 24, marginBottom: 8 }}><strong>1.1</strong>&nbsp; Terms not otherwise defined in this Assignment shall have the meaning given to them in the Sale Agreement.</p>
          <p style={{ marginLeft: 24 }}><strong>1.2</strong>&nbsp; For the purposes of this Assignment and in connection with the rights and monies assigned under it, the parties acknowledge and agree that any reference in the Agreement to the "maintenance exclusive element" of the Contracted Minimum Payments is intended to refer to the Hire Charges (as defined in the Agreement), being the element payable for the hire of the Equipment and excluding any maintenance, service or supply charges.</p>
        </div>

        <div style={{ fontSize: 15, marginBottom: 16 }}>
          <p style={{ fontWeight: 700, marginBottom: 8 }}>2&nbsp;&nbsp;&nbsp;Assignment</p>
          <div style={{ marginLeft: 24 }}>
            <p style={{ marginBottom: 8 }}><strong>2.1</strong>&nbsp; The Seller with full title guarantee hereby assigns absolutely to the Buyer all the Seller's title, rights, interest and benefit, present or future, arising out of or in respect of the Contracts, including, but not limited to:</p>
            <div style={{ marginLeft: 24, marginBottom: 8 }}>
              <p style={{ marginBottom: 4 }}>(a)&nbsp; all claims for or in respect of Contract Rights and other rights and remedies in respect of such Contracts or the failure to pay the same and claims for interest thereon;</p>
              <p style={{ marginBottom: 4 }}>(b)&nbsp; all monies which are now or may at any time be or become due or owing by the relevant Lessee in respect of or arising out of the Contracts; and</p>
              <p>(c)&nbsp; the benefit of all common law and equitable rights reserved by the Seller and relating to the Contract Rights.</p>
            </div>
            <p style={{ marginBottom: 8 }}><strong>2.2</strong>&nbsp; The Seller warrants and represents that:</p>
            <div style={{ marginLeft: 24 }}>
              <p style={{ marginBottom: 4 }}>(a)&nbsp; the details of the Lessees and the particulars of the Equipment the subject of the Contracts and other information set out in the Contracts are correct in every respect;</p>
              <p style={{ marginBottom: 4 }}>(b)&nbsp; the Contracts comply with all relevant statutes and regulations and are fully valid and enforceable against and undisputed by the respective Lessees named therein;</p>
              <p style={{ marginBottom: 4 }}>(c)&nbsp; the Equipment has been fully insured against all usual risks and against third party claims and the insurance policy therefore is valid and in force and effect; and</p>
              <p>(d)&nbsp; the Contract Rights are free from all Encumbrances.</p>
            </div>
          </div>
        </div>

        <div style={{ fontSize: 15, marginBottom: 24 }}>
          <p style={{ fontWeight: 700, marginBottom: 8 }}>3&nbsp;&nbsp;&nbsp;Governing Law</p>
          <p style={{ marginLeft: 24 }}>This Assignment shall be construed and governed according to English law.</p>
        </div>

        {/* ── New page starts here ── */}
        <div style={{ pageBreakBefore: "always", breakBefore: "page" }}>
          <PdfHr />
          <p style={{ fontSize: 15, marginBottom: 16 }}>Executed and delivered as a deed by the parties or their duly authorised representatives:</p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
            <div style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: 16, fontSize: 13 }}>
              <p style={{ fontWeight: 700, marginBottom: 6 }}>Executed as a deed by (Seller)</p>
              <B v={data.sellerEntity} minW={200} />
              <p style={{ fontSize: 12, color: "#6b7280", margin: "8px 0 12px" }}>acting by two directors or by a director and a witness</p>
              <div style={{ marginBottom: 6 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>

                  <SignatureBox url={sig1Url} isPdfMode />
                </div>
                <p style={{ fontSize: 11, color: "#6b7280", marginBottom: 8 }}>Authorised signatory</p>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                  <span style={{ minWidth: 52 }}>Name</span>
                  <span style={{ ...pdfBox(data.sig1Name, { minW: 160 }), flex: 1 }}>{data.sig1Name || "—"}</span>
                </div>
              </div>
              <hr style={{ borderTop: "1px solid #d1d5db", margin: "10px 0" }} />
              <p style={{ fontWeight: 700, fontSize: 12, marginBottom: 8 }}>Director / Witness</p>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                <span style={{ minWidth: 52 }}>Signed</span>
                <SignatureBox url={sig2Url} isPdfMode />
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                <span style={{ minWidth: 52 }}>Name</span>
                <span style={{ ...pdfBox(data.sig2Name, { minW: 160 }), flex: 1 }}>{data.sig2Name || "—"}</span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8 }}>
                <span style={{ minWidth: 52, paddingTop: 3 }}>Address</span>
                <span style={{ flex: 1, whiteSpace: "pre-wrap", fontSize: 12, color: "#374151" }}>{data.witnessAddress}</span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ minWidth: 52 }}>Dated</span>
                <span style={{ ...pdfBox(data.sig2Date, { minW: 130 }), flex: 1 }}>{data.sig2Date || "—"}</span>
              </div>
            </div>

            <div style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: 16, fontSize: 13 }}>
              <p style={{ fontWeight: 700, marginBottom: 6 }}>Executed as a deed by</p>
              <p style={{ marginBottom: 6 }}>CF Corporate Finance Limited</p>
              <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 20 }}>acting by two directors or by a director and Witness</p>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 36 }}>
                <span style={{ minWidth: 80, fontSize: 12 }}>Director</span>
                <div style={{ flex: 1, borderBottom: "1px solid #374151", height: 22 }} />
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ minWidth: 80, fontSize: 12 }}>Director / Witness</span>
                <div style={{ flex: 1, borderBottom: "1px solid #374151", height: 22 }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm font-sans text-sm leading-relaxed space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-3xl font-light text-gray-800 mb-3">Form of Assignment</h2>
          <div className="flex items-center gap-2 text-sm font-bold flex-wrap">
            <span>This Assignment is made on</span>
            <DatePicker value={data.assignmentDate} onChange={() => {}} readOnly className="w-36" title="Payment Date from Document 2" />
          </div>
        </div>
        <FibreHeader />
      </div>
      <p className="font-bold">Between</p>
      <div className="flex items-start gap-2">
        <span className="shrink-0">(1)</span>
        <div className="flex-1 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <input className={inlineInput("w-64")} value={data.sellerEntity} onChange={(e) => onChange("sellerEntity", e.target.value)} placeholder="Fibre Telecoms UK Limited" />
            <span>whose registered office is at</span>
          </div>
          <input className={`${inputCls} w-full`} value={data.sellerAddress} onChange={(e) => onChange("sellerAddress", e.target.value)} />
          <span className="font-bold">(Seller) and</span>
        </div>
      </div>
      <div className="flex items-start gap-2">
        <span className="shrink-0">(2)</span>
        <p>CF Corporate Finance Limited (Company Number 05414774) whose registered office is at 2 Gresham Street, London, EC2V 7QP <strong>(Buyer, which definition shall include its assignees and successors).</strong></p>
      </div>
      <p className="font-bold">Whereas</p>
      <div className="flex items-start gap-2">
        <span className="shrink-0">(A)</span>
        <div className="flex-1">
          <div className="flex items-center gap-1 flex-wrap">
            <span>By a sale and purchase agreement dated</span>
            <span className="font-bold mx-1">2nd June 2026</span>
            <span className="font-bold">(Sale Agreement)</span>
            <span>the Seller has agreed to sell and the Buyer has agreed to purchase the Equipment and the Contract Rights and certain other rights and benefits described in the Sale Agreement in respect of the Contract as set out below:</span>
          </div>
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="w-36 shrink-0">Agreement Number</span>
              <input className={`${inputCls} flex-1 bg-gray-50`} value={data.agreementNumber} readOnly title="Contract ref. no. from Document 2" />
            </div>
            <div className="flex items-start gap-2">
              <span className="w-36 shrink-0 mt-1">Equipment (Model)</span>
              <input className={`${inputCls} flex-1 bg-gray-50`} value={schedule?.equipmentModel || ""} readOnly />
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-start gap-2">
        <span className="shrink-0">(B)</span>
        <p>Under the terms of the Sale Agreement, this Assignment is to be delivered to the Buyer.</p>
      </div>
      <div>
        <h3 className="text-base font-bold mb-2">1&nbsp;&nbsp;&nbsp;Definitions and interpretation</h3>
        <div className="space-y-3 ml-6">
          <p><span className="font-semibold">1.1</span>&nbsp; Terms not otherwise defined in this Assignment shall have the meaning given to them in the Sale Agreement.</p>
          <p><span className="font-semibold">1.2</span>&nbsp; For the purposes of this Assignment and in connection with the rights and monies assigned under it, the parties acknowledge and agree that any reference in the Agreement to the "maintenance exclusive element" of the Contracted Minimum Payments is intended to refer to the Hire Charges (as defined in the Agreement), being the element payable for the hire of the Equipment and excluding any maintenance, service or supply charges.</p>
        </div>
      </div>
      <div>
        <h3 className="text-base font-bold mb-3">2&nbsp;&nbsp;&nbsp;Assignment</h3>
        <div className="space-y-3 ml-6 text-sm">
          <p><span className="font-semibold">2.1</span>&nbsp; The Seller with full title guarantee hereby assigns absolutely to the Buyer all the Seller's title, rights, interest and benefit, present or future, arising out of or in respect of the Contracts, including, but not limited to:</p>
          <div className="ml-6 space-y-2 pt-4">
            <p>(a)&nbsp; all claims for or in respect of Contract Rights and other rights and remedies in respect of such Contracts or the failure to pay the same and claims for interest thereon;</p>
            <p>(b)&nbsp; all monies which are now or may at any time be or become due or owing by the relevant Lessee in respect of or arising out of the Contracts; and</p>
            <p>(c)&nbsp; the benefit of all common law and equitable rights reserved by the Seller and relating to the Contract Rights.</p>
          </div>
          <p><span className="font-semibold">2.2</span>&nbsp; The Seller warrants and represents that:</p>
          <div className="ml-6 space-y-2">
            <p>(a)&nbsp; the details of the Lessees and the particulars of the Equipment the subject of the Contracts and other information set out in the Contracts are correct in every respect;</p>
            <p>(b)&nbsp; the Contracts comply with all relevant statutes and regulations and are fully valid and enforceable against and undisputed by the respective Lessees named therein;</p>
            <p>(c)&nbsp; the Equipment has been fully insured against all usual risks and against third party claims and the insurance policy therefore is valid and in force and effect; and</p>
            <p>(d)&nbsp; the Contract Rights are free from all Encumbrances.</p>
          </div>
        </div>
      </div>
      <div>
        <h3 className="text-base font-bold mb-2">3&nbsp;&nbsp;&nbsp;Governing Law</h3>
        <p className="ml-6">This Assignment shall be construed and governed according to English law.</p>
      </div>
      <div className="border-t border-gray-300 pt-4">
        <p className="text-sm mb-4">Executed and delivered as a deed by the parties or their duly authorised</p>
        <div className="grid grid-cols-2 gap-8">
          <div className="border border-gray-300 p-4 rounded space-y-3">
            <p className="font-semibold">Executed as a deed by (Seller)</p>
            <input className={inlineInput("w-full")} value={data.sellerEntity} onChange={(e) => onChange("sellerEntity", e.target.value)} placeholder="Fibre Telecoms UK Limited" />
            <p className="text-xs text-gray-500">acting by two directors or by a director and a witness</p>
            <div className="space-y-2 mt-2">
              <div className="flex items-center gap-2"><span className="w-16 shrink-0 text-xs">Signed</span><SignatureBox url={sig1Url} /></div>
              <div className="flex items-center gap-2">
                <span className="w-16 shrink-0 text-xs">Name</span>
                <input className={inlineInput("flex-1")} value={data.sig1Name} onChange={(e) => onChange("sig1Name", e.target.value)} placeholder="NEIL MACLEOD" />
              </div>
              <p className="text-xs text-gray-500">Authorised signatory</p>
              <div className="flex items-center gap-2">
                <span className="w-16 shrink-0 text-xs">Dated</span>
                <DatePicker value={data.sig1Date} onChange={(v) => onChange("sig1Date", v)} className="flex-1" />
              </div>
              <hr className="border-gray-300 my-2" />
              <p className="text-xs font-semibold">Director / Witness</p>
              <div className="flex items-center gap-2"><span className="w-16 shrink-0 text-xs">Signed</span><SignatureBox url={sig2Url} /></div>
              <div className="flex items-center gap-2">
                <span className="w-16 shrink-0 text-xs">Name</span>
                <input className={inlineInput("flex-1")} value={data.sig2Name} onChange={(e) => onChange("sig2Name", e.target.value)} placeholder="JAMIE HOARE" />
              </div>
              <div className="flex items-start gap-2">
                <span className="w-16 shrink-0 text-xs mt-1">Address</span>
                <textarea className={`${inputCls} flex-1 h-16 resize-none text-xs`} value={data.witnessAddress} onChange={(e) => onChange("witnessAddress", e.target.value)} placeholder={"33 LEMARECHAL AVENUE,\nSOUTHAMPTON,\n3031 8LW"} />
              </div>
              <div className="flex items-center gap-2">
                <span className="w-16 shrink-0 text-xs">Dated</span>
                <DatePicker value={data.sig2Date} onChange={(v) => onChange("sig2Date", v)} className="flex-1" />
              </div>
            </div>
          </div>
          <div className="border border-gray-300 p-4 rounded space-y-3">
            <p className="font-semibold">Executed as a deed by</p>
            <p>CF Corporate Finance Limited</p>
            <p className="text-xs text-gray-500">acting by two directors or by a director and Witness</p>
            <div className="space-y-2 mt-2">
              <div className="flex items-center gap-2"><span className="w-20 shrink-0 text-xs">Director</span><div className="flex-1 border-b border-gray-400 h-6" /></div>
              <div className="flex items-center gap-2 mt-4"><span className="w-20 shrink-0 text-xs">Director / Witness</span><div className="flex-1 border-b border-gray-400 h-6" /></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// DOCUMENT 4 — Form of Notice of Assignment
// ═══════════════════════════════════════════════════════════════════════════════
const NoticeOfAssignment = ({ data, onChange, isPdfMode ,sig1Url, sig1Name}) => {
  if (isPdfMode) {
    const B = ({ v, minW = 140, bold = false }) => (
      <span style={pdfBox(v, { minW, bold })}>
        {v || <span style={{ color: "#9ca3af", fontStyle: "italic", fontSize: 12 }}>—</span>}
      </span>
    );

    return (
      <div
        style={pdfDocStyle}
      >
        <FibreHeader isPdfMode />
        <h2 style={{ fontSize: 26, fontWeight: 300, color: "#1f2937", marginBottom: 18 }}>Form of Notice of Assignment</h2>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
          <div style={{ display: "flex", gap: 8, fontSize: 15 }}>
            <span style={{ paddingTop: 4 }}>To:</span>
            <div style={pdfBox(data.noticeTo, { minW: 240, multiline: true })}>
              {data.noticeTo || <span style={{ color: "#9ca3af", fontStyle: "italic" }}>lessee name and address</span>}
            </div>
          </div>
          <div style={{ fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
            <span>Date</span>
            <B v={data.noticeDate} minW={140} />
          </div>
        </div>

        <p style={{ fontSize: 15, marginBottom: 12 }}>Dear Sirs,</p>

        <p style={{ fontSize: 15, marginBottom: 4 }}>
          We refer to <B v={data.referTo} minW={130} />{" "}
          dated <B v={data.referDated} minW={130} />{" "}
          <strong>(Agreement)</strong>
        </p>
        <p style={{ fontSize: 15, marginBottom: 16 }}>made between us and you.</p>

        <div style={{ fontSize: 15 }}>
          <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
            <span style={{ minWidth: 20, fontWeight: 700 }}>1</span>
            <p style={{ margin: 0 }}>Any reference to Agreement in this notice shall be deemed to include a reference to each and every hire schedule entered into pursuant to the terms and conditions of the Agreement.</p>
          </div>
          <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
            <span style={{ minWidth: 20, fontWeight: 700 }}>2</span>
            <div style={{ flex: 1 }}>
              <p style={{ margin: "0 0 8px" }}>
                We hereby give you notice that on <B v={data.noticeOn} minW={140} />
              </p>
              <div style={{ marginLeft: 16 }}>
                <p style={{ marginBottom: 8 }}>(a)&nbsp; we have sold and transferred all ownership rights in the equipment hired subject to the terms of the Agreement <strong>(Equipment)</strong> to CF Corporate Finance Limited <strong>(Assignee)</strong>;</p>
                <p>(b)&nbsp; the whole benefit of and all the right, title and interest, present and future, actual or contingent of us in, among other things, the rentals and other sums due and payable under the Agreement <strong>(Contract Rights)</strong> were assigned by us to the Assignee;</p>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
            <span style={{ minWidth: 20, fontWeight: 700 }}>3</span>
            <p style={{ margin: 0 }}>The assignment provides that the Assignee shall not have any obligations whatsoever under or in relation to the Agreement which remain our responsibility.</p>
          </div>
          <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
            <span style={{ minWidth: 20, fontWeight: 700 }}>4</span>
            <p style={{ margin: 0 }}>Unless otherwise notified by the Assignee, please continue to make all further payments due under the Agreement to us.</p>
          </div>
        </div>

      <p style={{ fontSize: 15, marginTop: 24, marginBottom: 12 }}>Yours faithfully</p>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <span style={{ minWidth: 60 }}>Signed</span>
          <SignatureBox url={sig1Url} isPdfMode />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ minWidth: 60 }}>Name</span>
            <B v={sig1Name} minW={200} />
        </div>

        <div style={{ fontSize: 15 }}>
          <p style={{ marginBottom: 6 }}>
            For and on behalf of{" "}
            <B v={data.noticeSellerName} minW={220} />
            {" "}(the Seller)
          </p>
         
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm font-sans text-sm leading-relaxed space-y-5">
      <FibreHeader />
      <h2 className="text-3xl font-light text-gray-800">Form of Notice of Assignment</h2>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-2">
          <span className="mt-1">To:</span>
          <textarea className={`${inputCls} w-64 h-24 resize-none`} value={data.noticeTo} onChange={(e) => onChange("noticeTo", e.target.value)} placeholder="Lessee name and address" />
        </div>
        <div className="flex items-center gap-2">
          <span>Date</span>
          <DatePicker value={data.noticeDate} onChange={(v) => onChange("noticeDate", v)} className="w-36" />
        </div>
      </div>
      <p>Dear Sirs,</p>
      <div className="flex items-center gap-2 flex-wrap">
        <span>We refer to</span>
        <input className={`${inputCls} w-40 bg-gray-50`} value={data.referTo} readOnly title="Contract ref. no. from Document 2" />
        <span>dated</span>
        <DatePicker value={data.referDated} onChange={() => {}} readOnly className="w-36" title="Payment Date from Document 2" />
        <span className="font-bold">(Agreement)</span>
      </div>
      <p>made between us and you.</p>
      <div className="space-y-4">
        <div className="flex gap-3"><span className="shrink-0">1</span><p>Any reference to Agreement in this notice shall be deemed to include a reference to each and every hire schedule entered into pursuant to the terms and conditions of the Agreement.</p></div>
        <div className="flex gap-3">
          <span className="shrink-0">2</span>
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span>We hereby give you notice that on</span>
              <DatePicker value={data.noticeOn} onChange={() => {}} readOnly className="w-36" title="Assignment Date / Payment Date from Document 2" />
            </div>
            <div className="ml-6 space-y-2">
              <p>(a)&nbsp; we have sold and transferred all ownership rights in the equipment hired subject to the terms of the Agreement <strong>(Equipment)</strong> to CF Corporate Finance Limited <strong>(Assignee)</strong>;</p>
              <p>(b)&nbsp; the whole benefit of and all the right, title and interest, present and future, actual or contingent of us in, among other things, the rentals and other sums due and payable under the Agreement <strong>(Contract Rights)</strong> were assigned by us to the Assignee;</p>
            </div>
          </div>
        </div>
        <div className="flex gap-3"><span className="shrink-0">3</span><p>The assignment provides that the Assignee shall not have any obligations whatsoever under or in relation to the Agreement which remain our responsibility.</p></div>
        <div className="flex gap-3"><span className="shrink-0">4</span><p>Unless otherwise notified by the Assignee, please continue to make all further payments due under the Agreement to us.</p></div>
      </div>
    <p className="mt-8">Yours faithfully</p>

      <div className="mt-4 flex items-center gap-2">
        <span className="w-16 shrink-0 text-xs">Signed</span>
        <SignatureBox url={sig1Url} />
      </div>
       <div className="flex items-center gap-2 flex-wrap">
          <span className="w-16 shrink-0 text-xs">Name</span>
          <span className="text-sm">{sig1Name}</span>
        </div>

      <div className="mt-6 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span>For and on behalf of</span>
          <input className={inlineInput("w-64")} value={data.noticeSellerName} onChange={(e) => onChange("noticeSellerName", e.target.value)} placeholder="Fibre Telecoms UK Limited" />
          <span>(the Seller)</span>
        </div>
       
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════════════
// DOCUMENT 6 — Data Protection Notice
// ═══════════════════════════════════════════════════════════════════════════════
const DataProtectionNotice = ({ isPdfMode }) => {
  const s = { fontSize: 13, color: "#374151", lineHeight: 1.75, marginBottom: 10 };
  const h = { fontSize: 15, fontWeight: 700, color: "#111827", marginBottom: 6, marginTop: 16 };
  const bullet = { display: "flex", gap: 8, marginBottom: 4, fontSize: 13, color: "#374151" };

  const inner = (
    <>
      <FibreHeader isPdfMode={isPdfMode} />
      <h2 style={{ fontSize: 23, fontWeight: 700, color: "#1f2937", marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>
        Important - Use of Your Information
      </h2>
      <PdfHr />
      <p style={s}>CF Corporate Finance Limited ("we", "us", "our") is committed to processing your personal data in accordance with EU data protection laws. This Data Protection Notice aim to give you information on how your personal data (i.e., information which directly or indirectly identifies you) are processed by us. For the purposes of EU data protection laws, we are a data controller.</p>
      <p style={s}>This notice is supplemental to the data protection processing notice provided before your proposal for finance was submitted to us. Some aspects of the processing set out in this notice were covered by that earlier notice, but others are only relevant now that your application for finance has been approved in principle and we intend to enter into an agreement with you.</p>
      <p style={s}>Further details of how we will manage your data can be found at <span style={{ color: "#2563eb" }}>www.investec.com/en_gb/legal/UK/data-protection.html</span></p>
      <p style={h}>Data That May Be Collected.</p>
      <p style={s}>We may collect certain personal data with respect to you as a customer, including, without limitation your name, address, date of birth, contact details, credit reference data, financial and employment details, banking and credit card details, and details of your business. We collect some of this data from third parties, for example credit reference agencies.</p>
      <p style={s}>Where a corporate entity is entering into this contract we will collect personal data about the individuals who are directors and shareholders of the business from credit reference agencies where this data is held publicly, such as at Companies House. This notice also applies to the processing of such personal data and use of the word 'you' in this notice will encompass such individuals.</p>
      <p style={s}>We may process sensitive personal data (including, for example, information revealing an individual's physical or mental health). Where sensitive personal data are processed we will obtain your explicit consent for the processing.</p>
      <p style={h}>Use of Personal Data.</p>
      <p style={s}>We will use your personal data for: provision of products and services, credit and AML risk assessment, assessing ongoing credit performance, recoveries, collections, insurance administration, profiling for marketing purposes, market research and product development, statistical analysis, marketing, fraud prevention and detection and otherwise as necessary to comply with applicable laws, regulations and/or codes of practice. The processing of personal data may be necessary for the performance of a contractual relationship, compliance with a legal obligation, or where it is in the legitimate interests of the Investec Group.</p>
      <p style={h}>Disclosure to Certain Third Parties.</p>
      <p style={s}>We may disclose certain personal data: (i) within the Asset Finance Group of Investec and to other affiliates in the Investec Group; (ii) to our brokers and dealers / suppliers, professional advisors and service providers (including, information technology systems providers and recovery agents); (iii) to courts, governmental and non-governmental regulators and ombudsmen; (iv) to fraud prevention agencies and law enforcement agencies;</p><p style={s}>(v) to any third party that acquires, or is interested in acquiring, all or part of Investec's assets or shares, or that succeeds Investec in carrying on all or a part of its business, whether by merger, acquisition, reorganization or otherwise; and (vi) as otherwise required or permitted by law.</p>
      <p style={s}>We may also pass your Personal Data on to other Investec Group Companies and/or any relevant third party and both we and/or they may use it for any purpose linked to any sale of and/or granting of security over the agreement we have with you. In such circumstances, such third parties may also use and/or disclose your Personal Data to any third party that they ask to assist them with the preparation for and/or completion of any such sale and/or granting of security; they may also, once such sale and/or granting of security is completed, use and/or disclose your Personal Data to third parties for any of the other purposes which we have outlined in this notice in the same way as if they had entered into the agreement with you instead of us.</p>
      <p style={s}>The personal information we have collected will be shared with fraud prevention agencies who will use it to prevent fraud and money-laundering and to verify your identity. If fraud is detected you could be refused certain services, finance or employment. Further details of how your information will be used by us and these fraud prevention agencies, and your data protection rights can be found on the Investec website at <span style={{ color: "#2563eb" }}>www.investec.com/en_gb/Legal/UK/fraud-prevention-notice.html</span></p>
      <p style={s}>In order to process your application, we will perform credit and identity checks on you with one or more credit reference agencies ("CRAs"). To do this, we will supply your personal information to CRAs and they will give us information about you, even if your application does not proceed or is unsuccessful. This will include information from your credit application and about your financial situation and financial history. CRAs will supply to us both public (including the electoral register) and shared credit, financial situation and financial history information and fraud prevention information.</p>
      <p style={s}>We will use this information to:</p>
      {[
        "Assess your creditworthiness;",
        "Verify the accuracy of the data you have provided to us;",
        "Prevent criminal activity, fraud and money laundering;",
        "Manage your account(s);",
        "Trace and recover debts; and",
        "Ensure any offers provided to you are appropriate to your circumstances.",
      ].map((item) => (
        <div key={item} style={bullet}>
          <span style={{ marginTop: 2, flexShrink: 0 }}>•</span>
          <span>{item}</span>
        </div>
      ))}
      <p style={{ ...s, marginTop: 8 }}>We will continue to exchange information about you with CRAs while you have a relationship with us. We will also inform the CRAs about your settled accounts. If you borrow and do not repay in full and on time, CRAs will record the outstanding debt. This information may be supplied to other organisations by CRAs. When CRAs receive a search from us they will place a search footprint on your credit file that may be seen by other lenders.</p>
      <p style={{ ...s, marginTop: 8 }}>If you are making a joint application, or tell us that you have a spouse or financial associate, we will link your records together, so you should make sure you discuss this with them, and share with them this information, before lodging the application. CRAs will also link your records together and these links will remain on your and their files until such time as you or your partner successfully files for a disassociation with the CRAs to break that link.</p>
      <p style={s}>The identities of the CRAs, their role also as fraud prevention agencies, the data they hold, the ways in which they use and share personal information, data retention periods and your data protection rights with the CRAs are explained in more detail at each of the three CRAs websites – clicking on any of these three links will take you to the same CRAIN document:</p>
      {[
        { label: "Callcredit", url: "http://www.callcredit.co.uk/crain" },
        { label: "Equifax", url: "https://www.equifax.co.uk/crain" },
        { label: "Experian", url: "http://www.experian.co.uk/crain" },
      ].map(({ label, url }) => (
        <div
          key={label}
          style={{
            ...bullet,
            breakInside: "avoid",
            pageBreakInside: "avoid",
          }}
        >
          <span style={{ marginTop: 2, flexShrink: 0 }}>•</span>
          <span>
            {label}{" "}
            <span style={{ color: "#2563eb" }}>{url}</span>
          </span>
        </div>
      ))}
      <p style={h}>Transfer of Personal Data Outside the European Economic Area ("EEA").</p>
      <p style={s}>We may transfer your personal data to recipients (including affiliates in the Investec Group) located in countries outside of the EEA, including in South Africa, which may not have data privacy laws equivalent to those in the EEA. In those instances, we will take all necessary steps to protect your personal data in accordance with applicable data privacy laws.</p>
      <p style={h}>Your rights.</p>
      <p style={s}>Under applicable EU data privacy laws, you may have a right to: (i) request access to and rectification or erasure of your personal data; (ii) obtain restriction of processing or to object to processing of your personal data; and (iii) data portability (i.e. to request the transfer of personal data from one data controller to another in certain circumstances). If you wish to exercise any of these rights you should contact the Data Privacy Officer as described below. You also have the right to lodge a complaint about the processing of your personal data with your local data protection authority.</p>
      <p style={s}>We rely on automated credit assessment based on the personal data you provide to us and data which we obtain from our credit reference agency or similar sources about your credit profile or history. The outcome of this process can result in an automated decline of your application where it does not meet our acceptance criteria. We review this acceptance criteria regularly to ensure fairness in the decisions made. You have a right to ask us to manually review any decision taken in this manner.</p>
      <p style={s}>In addition we may use automated profiling to identify whether other products or services may be of use to you when we consider marketing campaigns. You have a right to object to this automated profiling. This is separate to the right you have to object to receive marketing.</p>
      <p style={h}>Security.</p>
      <p style={s}>We will take steps to protect your personal data against loss or theft, as well as from unauthorized access, disclosure, copying, use or modification, regardless of the format in which it is held.</p>
      <p style={{ ...h, marginTop: 8 }}>Retention.</p>
      <p style={s}>We will normally retain your personal data for a period of 7 years from the latest date on which we have a financial arrangement in place with you. Under certain circumstances this period may vary if we consider it necessary for good operational or commercial reasons, or in order to meet other legal obligations.</p>
      <p style={h}>Enquiries, Requests of Concerns.</p>
      <p style={s}>All enquiries, requests or concerns regarding this Notice or relating to the processing of Personal Data, should be sent to the Data Privacy Officer using the following contact details <span style={{ color: "#2563eb" }}>AFGDataprivacy@investec.co.uk</span></p>
      <PdfHr style={{ marginTop: 24 }} />
      <p style={{ fontSize: 12, color: "#9ca3af", textAlign: "center" }}>
        This Data Protection Notice is issued by CF Corporate Finance Limited in connection with the Master Sale and Purchase Agreement with Fibre Telecoms UK Limited.
      </p>
    </>
  );

  if (isPdfMode) {
    return <div style={pdfDocStyle}>{inner}</div>;
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm font-sans text-sm leading-relaxed">
      {inner}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════════════
// MAIN — AssignmentLetterTab
// ═══════════════════════════════════════════════════════════════════════════════
const AssignmentLetterTab = ({ selectedConnectionId }) => {
  const [isSending, setIsSending] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [sendStatus, setSendStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [termMonths, setTermMonths] = useState(0);
  const [signatures, setSignatures] = useState({}); // { jamie: url, neil: url }
  // isPdfMode controls ALL field rendering (inputs → static divs) for both
  // download and Adobe Sign sending — mirrors FibreContractForm pattern
  const [isPdfMode, setIsPdfMode] = useState(false);

  const doc1Ref = useRef(null);
  const doc2Ref = useRef(null);
  const doc3Ref = useRef(null);
  const doc4Ref = useRef(null);
  const doc5Ref = useRef(null);
  const doc6Ref = useRef(null);

  const [offer, setOffer] = useState({
    offerDate: "",
    agreementDate: "2nd June 2026",
    offerPrice: "",
    sellerName: "Fibre Telecoms UK Limited",
  });

  const [schedule, setSchedule] = useState({
    scheduleDated: "",
    offerLetterDated: "",
    contractRef: "",
    contractDated: "",
    serviceAgreement: "no",
    lessee: "",
    surety: "N/A",
    equipmentMake: "",
    equipmentModel: "",
    quantity: "",
    supplier: "Fibre Telecoms UK Limited",
    offerPriceExVat: "",
    paymentDate: "",
    leaseFrom: "",
    leaseTo: "",
    payments: "",
    paymentMonthly: true,
    paymentQuarterly: false,
    paymentAnnually: false,
    maintenance: "0.00",
    docFee: "0.00",
    directDebit: "yes",
    specialConditions: "",
  });

  const [assignment, setAssignment] = useState({
    assignmentDate: "",
    sellerEntity: "Fibre Telecoms UK Limited",
    sellerAddress:
      "Kingfisher House, Rownhams Lane, North Baddesley, Southampton, Hampshire, England, SO52 9LP",
    saleAgreementDate: "2nd June 2026",
    agreementNumber: "",
    equipmentDesc: "",
    sig1Name: "NEIL MACLEOD",
    sig1Date: "",
    sig2Name: "JAMIE HOARE",
    sig2Date: "",
    witnessAddress: "33 LEMARECHAL AVENUE,\nSOUTHAMPTON,\n3031 8LW",
  });

  const [notice, setNotice] = useState({
    noticeTo: "",
    noticeDate: "",
    referTo: "",
    referDated: "",
    noticeOn: "",
    noticeSellerName: "Fibre Telecoms UK Limited",
  });

  // ─── Load data ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedConnectionId) return;
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const { data } = await getSystemConfig(selectedConnectionId);
        if (!data) return;
        const contractDateFormatted = formatToDDMMYYYY(data.contract_date);
        const fullAddress = [
          data.connection_name,
          data.registered_address_line_1,
          data.registered_address_line_2,
          data.registered_address_line_3,
          data.postcode,
        ]
          .filter(Boolean)
          .join("\n");
        const hwItems = Array.isArray(data.hardwareItems) ? data.hardwareItems : [];
        const addlHw = data.additionalHardware
          ? Array.isArray(data.additionalHardware)
            ? data.additionalHardware
            : Object.values(data.additionalHardware)
          : [];
        const equipDesc = buildEquipmentDesc(hwItems, addlHw);
        const totalProductsQty  = buildProductsSectionTotalQty(hwItems, addlHw);
        const lease = data.leaseCalculatorData || {};
        const adjustedRental = lease.adjustedRental || "";
        const paymentProfile = lease.paymentProfile || "";
        const maintenanceCost = lease.current_maintenance || "0.00";
        if (paymentProfile) setTermMonths(parseInt(paymentProfile, 10));
        let leaseEndDateStr = "";
        if (data.contract_date && paymentProfile) {
          const start = new Date(data.contract_date);
          if (!isNaN(start)) {
            const end = new Date(start);
            end.setMonth(end.getMonth() + parseInt(paymentProfile, 10));
            leaseEndDateStr = formatToDDMMYYYY(end.toISOString());
          }
        }
        setOffer((p) => ({ ...p, offerDate: "", offerPrice: "" }));
        setSchedule((p) => ({
          ...p,
          lessee: fullAddress,
          equipmentMake: "GAMMA, YEALINK, DRAYTEK",
          equipmentModel: "",
          quantity: totalProductsQty,
          paymentDate: contractDateFormatted,
          leaseFrom: contractDateFormatted,
          leaseTo: leaseEndDateStr,
          payments: String(adjustedRental),
          maintenance: String(maintenanceCost),
        }));
        setAssignment((p) => ({
          ...p,
          assignmentDate: contractDateFormatted,
          saleAgreementDate: contractDateFormatted,
          agreementNumber: "",
          equipmentDesc: equipDesc,
          sig1Date: contractDateFormatted,
          sig2Date: contractDateFormatted,
        }));
        setNotice((p) => ({
          ...p,
          noticeTo: fullAddress,
          noticeDate: contractDateFormatted,
          referTo: "",
          referDated: contractDateFormatted,
          noticeOn: contractDateFormatted,
        }));
      } catch (err) {
        console.error("AssignmentLetterTab: failed to load config", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [selectedConnectionId]);

  // ─── Patch helpers ──────────────────────────────────────────────────────────
  const patchOffer = (k, v) => setOffer((p) => ({ ...p, [k]: v }));
  const patchAssign = (k, v) => setAssignment((p) => ({ ...p, [k]: v }));
  const patchNotice = (k, v) => setNotice((p) => ({ ...p, [k]: v }));
  const patchSchedule = (k, v) => {
    setSchedule((p) => ({ ...p, [k]: v }));
    if (k === "paymentDate") {
      setAssignment((p) => ({ ...p, assignmentDate: v }));
      setNotice((p) => ({ ...p, referDated: v, noticeOn: v }));
    }
    if (k === "contractRef") {
      setAssignment((p) => ({ ...p, agreementNumber: v }));
      setNotice((p) => ({ ...p, referTo: v }));
    }
  };

  // ─── Load signatures ────────────────────────────────────────────────────────
  useEffect(() => {
    getSignatures()
      .then(setSignatures)
      .catch((err) => console.error("Failed to load signatures", err));
  }, []);

  const matchSignature = (name) => {
    if (!name) return null;
    const key = name.trim().toLowerCase();
    const found = Object.keys(signatures).find((id) => key.includes(id));
    return found ? signatures[found] : null;
  };

  // ─── Download PDF ────────────────────────────────────────────────────────────
  // Sets isPdfMode=true so all inputs render as static text (no selectable fields)
const handleDownloadPdf = async () => {
  setIsDownloading(true);
  setIsPdfMode(true);
  await new Promise((r) => setTimeout(r, 300)); // let inputs swap to static text

  try {
    const docRefs = [
      { ref: doc1Ref, forceBreak: false },
      { ref: doc2Ref, forceBreak: true },
      { ref: doc3Ref, forceBreak: true },
      { ref: doc4Ref, forceBreak: true },
      { ref: doc6Ref, forceBreak: true },
    ];

    // Stitch all documents into one container, each starting on a new page
    const container = document.createElement("div");
    container.style.background = "#ffffff";

    docRefs.forEach(({ ref, forceBreak }) => {
      if (!ref.current) return;
      const clone = ref.current.cloneNode(true);
      clone.querySelectorAll(".no-pdf-export").forEach((el) => el.remove());
      if (forceBreak) {
        clone.style.pageBreakBefore = "always";
        clone.style.breakBefore = "page";
      }
      container.appendChild(clone);
    });

    const options = {
      // margin removed — each document already carries its own padding
      // (pdfDocStyle / compactDocStyle). Combining html2pdf's `margin`
      // option with CSS page-break mode double-accounts for spacing and
      // is the actual cause of the recurring blank pages.
      margin: 0,
      filename: `assignment-letter-${selectedConnectionId || "package"}.pdf`,
      image: { type: "jpeg", quality: 0.95 },
      html2canvas: {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        logging: false,
        letterRendering: true,
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
        compress: true,
        putOnlyUsedFonts: true,
        hotfixes: ["px_scaling"],
      },
      pagebreak: { mode: ["css"] },
    };

    await html2pdf().set(options).from(container).save();
  } catch (err) {
    console.error("PDF download failed:", err);
    alert("Error generating PDF. Please try again.");
  } finally {
    setIsPdfMode(false);
    setIsDownloading(false);
  }
};

  // ─── Send (Adobe Sign / email) ───────────────────────────────────────────────
  // Also sets isPdfMode=true so fields are static during HTML capture
  const handleSend = async () => {
    setIsSending(true);
    setSendStatus(null);
    setIsPdfMode(true);
    await new Promise((r) => setTimeout(r, 300));
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem("authToken");

      // Collect rendered HTML from all doc refs
      const docRefs = [doc1Ref, doc2Ref, doc3Ref, doc4Ref, doc5Ref, doc6Ref];
      const combinedHtml = docRefs
        .map((ref) => ref.current?.innerHTML || "")
        .join('<div style="page-break-before:always"></div>');

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <script src="https://cdn.tailwindcss.com"></script>
          <style>
            body { font-family: Arial, Helvetica, sans-serif; color: #111; background: #fff; }
          </style>
        </head>
        <body>${combinedHtml}</body>
        </html>
      `;

      const res = await fetch(`${API_BASE_URL}/assignment-letter/save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          connection_id: selectedConnectionId,
          html: htmlContent,
          offer,
          schedule,
          assignment,
          notice,
        }),
      });

      if (res.ok) {
        setSendStatus({ type: "success", message: "Assignment letter package sent successfully." });
      } else {
        const err = await res.json().catch(() => ({}));
        setSendStatus({ type: "error", message: err?.message || "Failed to send. Please try again." });
      }
    } catch {
      setSendStatus({ type: "error", message: "Network error. Please check your connection." });
    } finally {
      setIsPdfMode(false);
      setIsSending(false);
    }
  };

  // ─── Loading state ───────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500">
        <svg className="animate-spin h-6 w-6 mr-3 text-blue-600" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        Loading assignment data…
      </div>
    );
  }

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="mt-4 space-y-6">
       {/* Action buttons — hidden in PDF mode */}
      {!isPdfMode && (
        <div className="flex justify-end gap-3 pt-2 pb-8">
          
          <button
            onClick={handleDownloadPdf}
            disabled={isDownloading}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
              isDownloading
                ? "border-gray-200 text-gray-400 cursor-not-allowed"
                : "border-blue-300 text-blue-700 hover:bg-blue-50"
            }`}
          >
            <Download className="h-4 w-4" />
            {isDownloading ? "Generating PDF…" : "Download PDF"}
          </button>
          
        </div>
      )}
      {/* Header banner — hidden in PDF mode */}
      {!isPdfMode && (
        <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg flex items-center gap-3">
          <div className="bg-purple-100 w-10 h-10 rounded-full flex items-center justify-center shrink-0">
            <FileSignature className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h4 className="font-semibold text-purple-900">CF Corporate Finance — Assignment Letter Package</h4>
            <p className="text-xs text-purple-500">Data auto-populated from installation record. Edit any field below if needed.</p>
          </div>
        </div>
      )}

      <div>
        {/* ── Document 1 ── */}
        {!isPdfMode && <SectionDivider title="Document 1 — Form of Offer" />}
        <div ref={doc1Ref}>
           <FormOfOffer
              data={offer}
              onChange={patchOffer}
              isPdfMode={isPdfMode}
              sig1Url={matchSignature(assignment.sig1Name)}
              sig1Name={assignment.sig1Name}
            />
        </div>

        {/* ── Document 2 ── */}
        {!isPdfMode && <SectionDivider title="Document 2 — Details of the Offer (Schedule)" />}
        <div ref={doc2Ref}>
          <DetailsOfOffer
            data={schedule}
            onChange={patchSchedule}
            termMonths={termMonths}
            isPdfMode={isPdfMode}
          />
        </div>

        {/* ── Document 3 ── */}
        {!isPdfMode && <SectionDivider title="Document 3 — Form of Assignment" />}
        <div ref={doc3Ref}>
          <FormOfAssignment
            data={assignment}
            onChange={patchAssign}
            schedule={schedule}
            isPdfMode={isPdfMode}
            sig1Url={matchSignature(assignment.sig1Name)}
            sig2Url={matchSignature(assignment.sig2Name)}
          />
        </div>

        {/* ── Document 4 ── */}
        {!isPdfMode && <SectionDivider title="Document 4 — Form of Notice of Assignment" />}
        <div ref={doc4Ref}>
          <NoticeOfAssignment data={notice} onChange={patchNotice} isPdfMode={isPdfMode} sig1Url={matchSignature(assignment.sig1Name)}
  sig1Name={assignment.sig1Name} />
        </div>

       

        {/* ── Document 6 — no-break wrapper, inline style for PDF mode ── */}
        {!isPdfMode && <SectionDivider title="Document 5 — Use of Your Information" />}
        <div
          ref={doc6Ref}
        >
          <DataProtectionNotice isPdfMode={isPdfMode} />
        </div>
      </div>

      {/* Send status */}
      {sendStatus && !isPdfMode && (
        <div
          className={`flex items-start gap-3 p-4 rounded-lg border text-sm ${
            sendStatus.type === "success"
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          <span className="text-lg leading-none shrink-0">
            {sendStatus.type === "success" ? "✓" : "✕"}
          </span>
          <p>{sendStatus.message}</p>
        </div>
      )}

     
    </div>
  );
};

export default AssignmentLetterTab;